import React, { useState, useEffect } from 'react'
import TabbedDashboard from './TabbedDashboard'
import { Project, projectsAPI } from '../services/api'

const Dashboard: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error'>('synced')

  useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.getAll()
      if (response.success) {
        setProjects(response.data || [])
        setSyncStatus('synced')
      } else {
        setError('Failed to fetch projects')
        setSyncStatus('error')
      }
    } catch (err) {
      console.error('Error fetching projects:', err)
      setError('Failed to fetch projects')
      setSyncStatus('error')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProject = async (project: Project) => {
    try {
      setSyncStatus('syncing')
      if (project.id) {
        const response = await projectsAPI.update(project.id.toString(), project)
        if (response.success) {
          setProjects(prev => prev.map(p => p.id === project.id ? project : p))
          setSyncStatus('synced')
        } else {
          setError('Failed to update project')
          setSyncStatus('error')
        }
      }
    } catch (err) {
      console.error('Error updating project:', err)
      setError('Failed to update project')
      setSyncStatus('error')
    }
  }

  const handleAddProject = async (project: Project) => {
    try {
      setSyncStatus('syncing')
      const response = await projectsAPI.create(project)
      if (response.success) {
        setProjects(prev => [...prev, response.data!])
        setSyncStatus('synced')
      } else {
        setError('Failed to add project')
        setSyncStatus('error')
      }
    } catch (err) {
      console.error('Error adding project:', err)
      setError('Failed to add project')
      setSyncStatus('error')
    }
  }

  const handleDeleteProject = async (projectId: number) => {
    try {
      setSyncStatus('syncing')
      const response = await projectsAPI.delete(projectId.toString())
      if (response.success) {
        setProjects(prev => prev.filter(p => p.id !== projectId))
        setSyncStatus('synced')
      } else {
        setError('Failed to delete project')
        setSyncStatus('error')
      }
    } catch (err) {
      console.error('Error deleting project:', err)
      setError('Failed to delete project')
      setSyncStatus('error')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card-glass p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card-glass p-8 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={fetchProjects} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <TabbedDashboard
        projects={projects}
        onUpdateProject={handleUpdateProject}
        onAddProject={handleAddProject}
        onDeleteProject={handleDeleteProject}
      />
    </div>
  )
}

export default Dashboard 